/**
 * File: stillToBill.ts
 * Path: packages/config/src/invoices/stillToBill.ts
 * Description: Contract payments not invoiced yet: money the business is
 * owed under signed contracts but hasn't billed. This is never added to
 * Debtors (invoiced and unpaid): it is its own figure, spread over the next
 * 30, 60 and 90 days, with a "Due to bill" flag on any payment whose date has
 * passed with no invoice.
 *
 * A payment counts once an invoice is saved for its reference or a Zoho
 * invoice is linked to it. A payment marked received is history and left
 * out, unless the money was held as the client's credit because no invoice
 * existed yet: that one still needs its invoice.
 */

import { contractBalance, type PaymentReceipt } from "../contracts/contractReceipts";
import { contractSignedOn, workedOutDueOn, type PaymentDueDate } from "../contracts/contractDue";
import { toMonthlyDueDay } from "../contracts/contractDueDay";
import type { Contract } from "../contracts/contractTypes";
import { randToCents } from "../accounting/money";

export interface ToBillRow {
  contractId: string;
  contractReference: string;
  clientId: string | null;
  clientName: string;
  /** "IDK-C012-02-M03" */
  reference: string;
  label: string;
  cents: number;
  /** YYYY-MM-DD, or null while no date is known (on delivery, a stage). */
  dueOn: string | null;
  /** True when the client already paid it and the money waits as their credit for this invoice. */
  paidAhead: boolean;
  /** The date has come and there is no invoice: "Due to bill". */
  dueToBill: boolean;
}

export interface StillToBill {
  rows: ToBillRow[];
  /** Dates that have come with no invoice. */
  dueCents: number;
  /** Due in the next 1 to 30 days, 31 to 60, 61 to 90. */
  next30Cents: number;
  next60Cents: number;
  next90Cents: number;
  /** Further out than 90 days. */
  laterCents: number;
  /** No date known yet. */
  undatedCents: number;
  totalCents: number;
}

const DAY_MS = 86_400_000;
const dayNumber = (iso: string) => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;

const owesMoney = (c: Pick<Contract, "status">) => c.status === "client_signed" || c.status === "signed";

export const stillToBill = (input: {
  contracts: readonly Contract[];
  receipts: Readonly<Record<string, readonly PaymentReceipt[]>>;
  dueDates: Readonly<Record<string, readonly PaymentDueDate[]>>;
  /** Payment references an invoice already covers (saved and not void, or a linked Zoho invoice). */
  invoiced: ReadonlySet<string>;
  today: string;
}): StillToBill => {
  const rows: ToBillRow[] = input.contracts.filter(owesMoney).flatMap((contract) => {
    const receipts = input.receipts[contract.id] ?? [];
    const set = new Map((input.dueDates[contract.id] ?? []).map((d) => [d.reference, d.dueOn]));
    const signedOn = contractSignedOn(contract);
    return contractBalance(contract, [...receipts]).rows.flatMap((row): ToBillRow[] => {
      if (input.invoiced.has(row.reference)) return [];
      if (row.receipt && !row.receipt.held) return [];
      const cents = randToCents(row.amount);
      if (cents <= 0) return [];
      const dueOn = set.get(row.reference) ?? workedOutDueOn(row.part, signedOn, toMonthlyDueDay(contract.content.monthlyDueDay));
      return [
        {
          contractId: contract.id,
          contractReference: contract.reference,
          clientId: contract.clientId,
          clientName: contract.content.client.name,
          reference: row.reference,
          label: row.label,
          cents,
          dueOn,
          paidAhead: Boolean(row.receipt?.held),
          dueToBill: dueOn !== null && dueOn <= input.today,
        },
      ];
    });
  });
  rows.sort((a, b) => (a.dueOn ?? "9999").localeCompare(b.dueOn ?? "9999") || a.reference.localeCompare(b.reference));
  const sum = (pick: (r: ToBillRow) => boolean) => rows.filter(pick).reduce((s, r) => s + r.cents, 0);
  const daysOut = (r: ToBillRow) => (r.dueOn === null ? null : dayNumber(r.dueOn) - dayNumber(input.today));
  return {
    rows,
    dueCents: sum((r) => r.dueToBill),
    next30Cents: sum((r) => (daysOut(r) ?? -1) > 0 && (daysOut(r) ?? 0) <= 30),
    next60Cents: sum((r) => (daysOut(r) ?? 0) > 30 && (daysOut(r) ?? 0) <= 60),
    next90Cents: sum((r) => (daysOut(r) ?? 0) > 60 && (daysOut(r) ?? 0) <= 90),
    laterCents: sum((r) => (daysOut(r) ?? 0) > 90),
    undatedCents: sum((r) => r.dueOn === null),
    totalCents: sum(() => true),
  };
};
